import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_FONT,
  DEFAULT_FONT_SIZE,
  DEFAULT_MODE,
  DEFAULT_THEME,
  FONTS,
  FONT_SIZES,
  MODES,
  THEMES,
} from "./theme";
import type { FontId, FontSizeId, ThemeId, ThemeMode } from "./theme";

const THEME_KEY = "cenna.theme";
const FONT_KEY = "cenna.font";
const MODE_KEY = "cenna.mode";
const FONT_SIZE_KEY = "cenna.fontSize";
const HC_KEY = "cenna.highContrast";

function read<T extends string>(key: string, fallback: T, valid: readonly T[]): T {
  if (typeof window === "undefined") return fallback;
  let v = window.localStorage.getItem(key) as T | null;
  // Migrate legacy font-size id "default" → "small"
  if (key === FONT_SIZE_KEY && v === ("default" as T)) {
    v = "small" as T;
  }
  return v && valid.includes(v) ? v : fallback;
}

const THEME_IDS = THEMES.map((t) => t.id);
const FONT_IDS = FONTS.map((f) => f.id);
const MODE_IDS = MODES.map((m) => m.id);
const FONT_SIZE_IDS = FONT_SIZES.map((f) => f.id);

function resolveMode(mode: ThemeMode): "light" | "dark" {
  if (mode === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return mode;
}

export function useTheme() {
  const [theme, setThemeState] = useState<ThemeId>(() => read(THEME_KEY, DEFAULT_THEME, THEME_IDS));
  const [font, setFontState] = useState<FontId>(() => read(FONT_KEY, DEFAULT_FONT, FONT_IDS));
  const [mode, setModeState] = useState<ThemeMode>(() => read(MODE_KEY, DEFAULT_MODE, MODE_IDS));
  const [fontSize, setFontSizeState] = useState<FontSizeId>(() =>
    read(FONT_SIZE_KEY, DEFAULT_FONT_SIZE, FONT_SIZE_IDS)
  );
  const [highContrast, setHighContrastState] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(HC_KEY) === "1";
  });
  const [resolvedMode, setResolvedMode] = useState<"light" | "dark">(() =>
    typeof window !== "undefined" ? resolveMode(read(MODE_KEY, DEFAULT_MODE, MODE_IDS)) : "light"
  );

  useEffect(() => {
    window.localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    window.localStorage.setItem(FONT_KEY, font);
  }, [font]);

  useEffect(() => {
    window.localStorage.setItem(MODE_KEY, mode);
    setResolvedMode(resolveMode(mode));
  }, [mode]);

  useEffect(() => {
    window.localStorage.setItem(FONT_SIZE_KEY, fontSize);
  }, [fontSize]);

  useEffect(() => {
    window.localStorage.setItem(HC_KEY, highContrast ? "1" : "0");
  }, [highContrast]);

  useEffect(() => {
    if (mode !== "system") return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setResolvedMode(resolveMode("system"));
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [mode]);

  useEffect(() => {
    const el = document.documentElement;
    el.setAttribute("data-theme", theme);
    el.setAttribute("data-font", font);
    el.setAttribute("data-mode", resolvedMode);
    el.setAttribute("data-fs", fontSize);
    el.setAttribute("data-hc", highContrast ? "true" : "false");
  }, [theme, font, resolvedMode, fontSize, highContrast]);

  const applyToRoot = useCallback(
    (root: HTMLElement) => {
      root.setAttribute("data-theme", theme);
      root.setAttribute("data-font", font);
      root.setAttribute("data-mode", resolvedMode);
      root.setAttribute("data-fs", fontSize);
      root.setAttribute("data-hc", highContrast ? "true" : "false");
    },
    [theme, font, resolvedMode, fontSize, highContrast]
  );

  return {
    theme,
    font,
    mode,
    resolvedMode,
    fontSize,
    highContrast,
    setTheme: (t: ThemeId) => setThemeState(t),
    setFont: (f: FontId) => setFontState(f),
    setMode: (m: ThemeMode) => setModeState(m),
    setFontSize: (f: FontSizeId) => setFontSizeState(f),
    setHighContrast: (v: boolean) => setHighContrastState(v),
    applyToRoot,
  };
}
